import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { config } from "../config";
import { useAuthStore } from "../auth/store";
import { Card, Muted, PrimaryButton, SecondaryButton, StatusPill } from "./ui";

type Billing = {
  subscriptionStatus?: string;
  tokenBalance: number;
  subscriptionTokenBalance: number;
  complimentaryTokenBalance: number;
  monthlyTokenAllowance: number;
  tokensUsedThisPeriod: number;
  currentPeriodEnd?: { _seconds: number } | string;
};

const Grid = styled.div`
  display: grid;
  gap: 1rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
`;

const Big = styled.span`
  font-size: 2rem;
  font-weight: 700;
`;

const AdminCard = styled(Card)`
  margin-top: 0.25rem;
`;

const AdminForm = styled.form`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 720px) {
    grid-template-columns: minmax(0, 1fr) minmax(120px, 0.25fr) auto;
    align-items: end;
  }
`;

const Field = styled.div`
  display: grid;
  gap: 0.45rem;
`;

const FieldLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #344054;
`;

const Input = styled.input`
  min-width: 0;
  padding: 0.7rem 0.8rem;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  color: #172033;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #315fd6;
    box-shadow: 0 0 0 3px rgba(49, 95, 214, 0.12);
  }
`;


function formatBillingDate(value: { _seconds: number } | string): string {
  const date = typeof value === "string" ? new Date(value) : new Date(value._seconds * 1000);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString();
}

const BillingPage: React.FC = () => {
  const {
    state: { isAdmin, user },
  } = useAuthStore();
  const [billing, setBilling] = useState<Billing | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminAmount, setAdminAmount] = useState("10");
  const [adminStatus, setAdminStatus] = useState("");
  const [isGranting, setIsGranting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/billing/summary`, {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error(`Failed to load billing (${res.status})`);
      }

      const data = await res.json();
      setBilling(data.billing);
      setStatus("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch((e) => {
      console.error(e);
      setStatus("Could not load billing profile.");
      setLoading(false);
    });
  }, []);

  const startCheckout = async () => {
    try {
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/billing/checkout`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Checkout start failed (${res.status})`);
      }
      const data = await res.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setStatus("Checkout link was unavailable. Please try again.");
    } catch (e) {
      console.error(e);
      setStatus("Could not start checkout. Please try again.");
    }
  };

  const openPortal = async () => {
    try {
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/billing/portal`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Portal open failed (${res.status})`);
      }
      const data = await res.json();
      if (data.portalUrl) {
        window.location.href = data.portalUrl;
        return;
      }
      setStatus("Billing portal link was unavailable. Please try again.");
    } catch (e) {
      console.error(e);
      setStatus("Could not open billing portal. Please try again.");
    }
  };

  const grantCredits = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amount = Number(adminAmount);
    if (!Number.isInteger(amount) || amount < 1 || amount > 10_000) {
      setAdminStatus("Enter a whole number of credits from 1 to 10,000.");
      return;
    }

    setIsGranting(true);
    setAdminStatus("Granting complimentary credits...");
    try {
      const res = await fetch(`${config.REACT_APP_API_BASE_URL}/admin/credits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: adminEmail, amount }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        setAdminStatus(data?.error || `Credit grant failed (${res.status}).`);
        return;
      }

      setAdminStatus(
        `Granted ${data.granted} credits to ${data.target.email}. Complimentary balance: ${data.complimentaryTokenBalance}.`,
      );
      if (adminEmail.trim().toLowerCase() === user?.email?.trim().toLowerCase()) {
        await load();
      }
    } catch (error) {
      console.error(error);
      setAdminStatus("Could not grant credits. Please try again.");
    } finally {
      setIsGranting(false);
    }
  };

  if (loading) {
    return <Card><StatusPill>Loading</StatusPill><Muted>Fetching billing details...</Muted></Card>;
  }

  return (
    <Grid>
      <Card>
        <StatusPill>{billing?.subscriptionStatus || "inactive"}</StatusPill>
        <Muted style={{ marginTop: "0.65rem" }}>
          Tokens refresh every billing cycle. Each workout generation costs 1 token.
        </Muted>
      </Card>

      <Card>
        <Stat><Big>{billing?.tokenBalance ?? 0}</Big><span>tokens remaining</span></Stat>
        <Muted>
          Used {billing?.tokensUsedThisPeriod ?? 0} / {billing?.monthlyTokenAllowance ?? 0} this period.
        </Muted>
        {(billing?.complimentaryTokenBalance ?? 0) > 0 && (
          <Muted style={{ marginTop: "0.45rem" }}>
            {billing?.complimentaryTokenBalance} complimentary + {billing?.subscriptionTokenBalance ?? 0} subscription credits
          </Muted>
        )}
        {billing?.currentPeriodEnd && (
          <Muted>
            Renews on {formatBillingDate(billing.currentPeriodEnd)}.
          </Muted>
        )}
      </Card>

      <Card>
        <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
          <PrimaryButton onClick={startCheckout}>Start monthly plan</PrimaryButton>
          <SecondaryButton onClick={openPortal}>Manage subscription</SecondaryButton>
        </div>
        {status && <Muted style={{ marginTop: "0.8rem" }}>{status}</Muted>}
      </Card>

      {isAdmin && (
        <AdminCard>
          <StatusPill>Admin</StatusPill>
          <Muted style={{ marginTop: "0.65rem" }}>
            Grant complimentary generation credits to a registered LaceAI user. This does not change their Stripe subscription.
          </Muted>
          <AdminForm onSubmit={grantCredits}>
            <Field>
              <FieldLabel htmlFor="admin-credit-email">User email</FieldLabel>
              <Input
                id="admin-credit-email"
                type="email"
                value={adminEmail}
                onChange={(event) => setAdminEmail(event.target.value)}
                placeholder="runner@example.com"
                required
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="admin-credit-amount">Credits</FieldLabel>
              <Input
                id="admin-credit-amount"
                type="number"
                min="1"
                max="10000"
                step="1"
                value={adminAmount}
                onChange={(event) => setAdminAmount(event.target.value)}
                required
              />
            </Field>
            <PrimaryButton type="submit" disabled={isGranting}>
              {isGranting ? "Granting..." : "Grant Credits"}
            </PrimaryButton>
          </AdminForm>
          {adminStatus && <Muted style={{ marginTop: "0.8rem" }}>{adminStatus}</Muted>}
        </AdminCard>
      )}
    </Grid>
  );
};

export default BillingPage;
